<template>
    <div>
        <DataTable
            :header="$t('deviationsFromRecommendedPrice')"
            :fields="fields"
            :items="chartsData"
            :loading="request.getLoading()"
            :error="request.getError()"
            :hover="false"
            class="mb-4"
            thead-class="d-none"
            table-class="table-layout-fixed"
            :overflow="false"
        >
            <template #cell(chart)="{item}">
                <div class="position-relative">
                    <ButtonIcon
                        class="edit-btn wide"
                        size="sm"
                        :disabled="requestUpdatePrice.getLoading(item.product.id)"
                        icon="edit-2"
                        @click="editPrice(item.product)"
                    >
                        {{ item.product.price ? $format.currency(item.product.price) : $t('giveThePrice') }}
                    </ButtonIcon>
                    <EChart :options="item" />
                </div>
            </template>
        </DataTable>
        <ModalEditRecommendedPrice />
    </div>
</template>



<script>
    import EChart from '@/components/common/EChart.vue';
    import DataTable from '@/components/common/DataTable.vue';
    import ModalEditRecommendedPrice from './ModalEditRecommendedPrice.vue';

    export default {
        name: 'DeviationsFromRecommendedPrice',
        components: {
            EChart,
            DataTable,
            ModalEditRecommendedPrice,
        },
        inject: {
            parentComponent: {
                default: null,
            },
        },
        provide() {
            return {
                parentComponent: this,
            };
        },
        data() {
            return {
                request: this.parentComponent.requestData,
                requestUpdatePrice: this.parentComponent.requestUpdatePrice,
            };
        },
        computed: {
            fields() {
                return [{ key: 'chart', label: '' }];
            },
            data() {
                return this.parentComponent.data;
            },
            chartsData() {
                if (!this.data || !this.data.products) {
                    return [];
                }

                return Object.keys(this.data.products).map(productKey => {
                    const productData = this.data.products[productKey];
                    const product = productData.product;
                    const priceArray = productData.price;
                    const recommendedPrice = product.price;

                    // Dynamiczne pobieranie nazw sklepów (wszystkie klucze poza "date")
                    const storeSet = new Set();
                    priceArray.forEach(obj => {
                        Object.keys(obj).forEach(key => {
                            if (key !== 'date') {
                                storeSet.add(key);
                            }
                        });
                    });
                    const stores = Array.from(storeSet).sort();

                    // Dla każdego sklepu wyliczamy średnią, minimalną i maksymalną cenę
                    const storeStats = stores.map(store => {
                        let sum = 0;
                        let count = 0;
                        let min = Infinity;
                        let max = -Infinity;
                        priceArray.forEach(obj => {
                            const val = obj[store];
                            if (val !== null && val !== undefined) {
                                sum += val;
                                count++;
                                if (val < min) min = val;
                                if (val > max) max = val;
                            }
                        });
                        const avg = count > 0 ? sum / count : null;
                        if (min === Infinity) min = null;
                        if (max === -Infinity) max = null;
                        const deviation = avg !== null && recommendedPrice !== null ? avg - recommendedPrice : null;
                        const deviationPct = deviation !== null && recommendedPrice !== 0 ? (deviation / recommendedPrice) * 100 : null;
                        return { store, avg, min, max, deviation, deviationPct };
                    });

                    // Dane dla serii linii – dla każdego sklepu cena rekomendowana
                    const recommendedSeriesData = stores.map(() => recommendedPrice);
                    // Dane dla serii scatter – obiekty zawierające dodatkowe informacje potrzebne w tooltipie
                    const scatterData = storeStats.map(stat => ({
                        value: stat.avg,
                        store: stat.store,
                        avg: stat.avg,
                        min: stat.min,
                        max: stat.max,
                        deviation: stat.deviation,
                        deviationPct: stat.deviationPct,
                    }));

                    return {
                        product,
                        grid: {
                            top: '80px',
                        },
                        title: {
                            text: product.name,
                            subtext: `EAN: ${product.ean}`,
                            textStyle: {
                                fontSize: 14,
                            },
                        },
                        tooltip: {
                            trigger: 'axis',
                            formatter: params => {
                                const seriesDev = params.find(series => series.seriesType === 'scatter');
                                const seriesRec = params.find(series => series.seriesType === 'line');
                                if (seriesDev?.data) {
                                    const d = seriesDev.data;
                                    const isValue = num => num !== null && num !== undefined;
                                    const formatCurrency = num => (isValue(num) ? this.$format.currency(num) : '-');
                                    const formatPercent = num => (isValue(num) ? this.$format.percent(num) : '-');
                                    const devPctStr = d.deviationPct !== null ? `${(d.deviationPct >= 0 ? '+' : '') + formatPercent(d.deviationPct)}` : '-';
                                    const devStr = d.deviation !== null ? `${(d.deviation >= 0 ? '+' : '') + formatCurrency(d.deviation)}` : '-';
                                    const deviationStr = d.deviation !== null ? `${devPctStr} (${devStr})` : '-';
                                    const tooltipItemsHtml = [
                                        { title: this.$t('price'), value: formatCurrency(d.avg) },
                                        { title: this.$t('deviation'), value: deviationStr },
                                        { title: this.$t('minimumPrice'), value: formatCurrency(d.min) },
                                        { title: this.$t('maximumPrice'), value: formatCurrency(d.max) },
                                        { title: this.$t('recommendedPrice'), value: formatCurrency(seriesRec.value) },
                                    ].reduce((acc, item) => `${acc}<div style="display:flex;gap:10px;">${item.title}: <strong style="margin-left:auto">${item.value}</strong></div>`, '');
                                    return `
                                        <div style="display:flex;flex-direction:column;gap:3px;">
                                            <div style="margin-bottom:2px;"><strong>${d.store}</strong></div>
                                            ${tooltipItemsHtml}
                                        </div>
                                    `;
                                }
                                return '';
                            },
                        },
                        xAxis: {
                            type: 'category',
                            data: stores,
                        },
                        yAxis: {
                            type: 'value',
                        },
                        series: [
                            {
                                name: this.$t('recommendedPrice'),
                                type: 'line',
                                data: recommendedSeriesData,
                                lineStyle: { color: 'black' },
                                symbol: 'none',
                                areaStyle: null,
                            },
                            {
                                name: this.$t('deviation'),
                                type: 'scatter',
                                data: scatterData,
                                itemStyle: { color: 'green' },
                            },
                        ],
                    };
                });
            },
        },
        methods: {
            editPrice(item) {
                this.$root.$emit('bv::show::modal', 'modal-edit-recommended-price');
                this.$root.$emit('modal-edit-recommended-price', item);
            },
        },
    };
</script>



<style lang="scss" scoped>
    .edit-btn {
        position: absolute;
        right: 10px;
        top: 10px;
        z-index: 10;
    }
</style>

